import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

const STORAGE_KEY = "kirtan-finder-auth-v1";

export type AuthUser = {
  id: string;
  email: string;
  displayName?: string;
  roles: string[];
};

type Session = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
};

type AuthContextValue = {
  user: AuthUser | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function restoreSession(): Session | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value ? (JSON.parse(value) as Session) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => restoreSession());

  const save = useCallback((next: Session | null) => {
    setSession(next);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // The session remains usable in memory when browser storage is unavailable.
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Could not sign in.");
      save(payload as Session);
    },
    [save],
  );

  const logout = useCallback(async () => {
    const refreshToken = session?.refreshToken;
    save(null);
    if (!refreshToken) return;
    await fetch("/api/auth/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    }).catch(() => {});
  }, [save, session?.refreshToken]);

  const authFetch = useCallback(
    async (input: RequestInfo | URL, init: RequestInit = {}) => {
      const makeRequest = (accessToken: string) =>
        fetch(input, {
          ...init,
          headers: {
            ...Object.fromEntries(new Headers(init.headers).entries()),
            Authorization: "Bearer " + accessToken,
          },
        });

      if (!session) return fetch(input, init);
      let response = await makeRequest(session.accessToken);
      if (response.status !== 401) return response;

      const refreshResponse = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      if (!refreshResponse.ok) {
        save(null);
        return response;
      }

      const tokens = await refreshResponse.json();
      const next = { ...session, ...tokens } as Session;
      save(next);
      response = await makeRequest(next.accessToken);
      return response;
    },
    [save, session],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user: session?.user || null, login, logout, authFetch }),
    [authFetch, login, logout, session?.user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}
